import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, CanActivateChildFn, CanLoadFn, Route, Router, RouterStateSnapshot, UrlSegment } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AuthenticationService } from '../services/auth.service';
import { SpinnerService } from '../services/spinner.service';
import { ROLES } from 'src/app/app-const';

// Functional Guard for CanActivate
export const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> | Promise<boolean> | boolean => {
  const authService = inject(AuthenticationService);
  const router = inject(Router);
  const spinnerService = inject(SpinnerService);

  const redirectUrl = state.url;
  const roles = route.data?.['roles'] as number[] | undefined;

  return checkAuth(redirectUrl, roles || [], authService, router, spinnerService);
};

// Functional Guard for CanActivateChild
export const authGuardChild: CanActivateChildFn = (
  childRoute: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> | Promise<boolean> | boolean => {
  const authService = inject(AuthenticationService);
  const router = inject(Router);
  const spinnerService = inject(SpinnerService);

  const redirectUrl = state.url;
  return checkAuth(redirectUrl, childRoute.data?.roles || [], authService, router, spinnerService);
};

// Functional Guard for CanLoad
export const authGuardLoad: CanLoadFn = (
  route: Route,
  segments: UrlSegment[]
): Observable<boolean> | Promise<boolean> | boolean => {
  const authService = inject(AuthenticationService);
  const router = inject(Router);
  const spinnerService = inject(SpinnerService);

  return checkAuth('/', route.data?.roles || [], authService, router, spinnerService);
};

// Shared logic for authentication check
function checkAuth(
  redirectURL: string,
  roles: number[],
  authService: AuthenticationService,
  router: Router,
  spinnerService: SpinnerService
): Observable<boolean> {
  spinnerService.showSpinner();

  return authService.identity().pipe(
    map((user) => {
      spinnerService.hideSpinner();

      if (!user) {
        router.navigate(['/account/login'], { queryParams: { redirectURL } });
        return false;
      }

      // If the main academy's subscription is expired/inactive, lock all portal routes.
      // Status IDs from backend Subscription model:
      //   0 = Active, 1 = Inactive, 2 = Expired, 3 = Near Expiry, 6 = Renewed Without Payment
      const academyStatus = (user as any)?.academy?.subscription_status;
      const isActive = academyStatus === 0 || academyStatus === 3 || academyStatus === 6;
      const isExpired = academyStatus === 1 || academyStatus === 2 || localStorage.getItem('vultSubscriptionExpired') === 'true';

      if (isActive) {
        localStorage.removeItem('vultSubscriptionExpired');
      } else if (isExpired && (user as any)?.roles?.includes(ROLES.AcademyAdmin)) {
        localStorage.setItem('vultSubscriptionExpired', 'true');
        router.navigate(['/account/subscription-expired']);
        return false;
      }

      const userRoles = user?.roles || [];
      if (userRoles.length === 0) {
        router.navigate(['/403']);
        return false;
      }

      if (roles) {
        let allowed = false;
        if (
          (!roles || roles.length === 0) ||
          roles.some((r) => userRoles.includes(r)) ||
          userRoles.includes(ROLES.AcademyAdmin) ||
          userRoles.includes(ROLES.FranchiseManager) ||
          userRoles.includes(ROLES.MasterAcademy)
        ) {
          allowed = true;
        }

        if (!allowed) {
          router.navigate(['/403']);
          return false;
        }
      }

      return true;
    }),
    catchError((error) => {
      spinnerService.hideSpinner();
      router.navigate(['/account/login'], { queryParams: { redirectURL } });
      return of(false);
    })
  );
}
